import { DialogRefAttrs } from '@app/components/DialogAlert';
import { TimeManager } from '@app/hooks/use-time-manager';
import { CliqueiAcheiData } from '@app/types';
import { RefObject, useEffect, useRef } from 'react';

const MIN_INTERVAL_TO_ALERT = 60 * 1000 * 15;

export default function useSetupAlerts(
  cliqueiAcheiData: CliqueiAcheiData | undefined,
  timeManager: TimeManager,
  shouldIgnoreSetup: boolean,
  dialogRef: RefObject<DialogRefAttrs>
) {
  const alertTimeout = useRef<number>(0);
  const updateDataTimeout = useRef<number>(0);
  const alertAboutClosingJustAppeared = useRef(false);

  const isOpen = timeManager.isOpen();

  useEffect(() => {
    // console.log('\n');
    // console.log('Time manager is ready: ', timeManager.isReady);
    // console.log('Open at:', timeManager.openAt?.toLocaleString());
    // console.log('Close at:', timeManager.closeAt?.toLocaleString());
    // console.log('Its Open:', timeManager.isOpen());
    // console.log('Week day: ', timeManager.currentSchedule?.DiaDaSemana);
    // console.log('Has passed business hours:', timeManager.hasPassedBusinessHours());
    // console.log('Has passed closing time: ', timeManager.hasPassedClosingTime());
    // console.log('Not open today: ', timeManager.notOpenToday());

    if (shouldIgnoreSetup || !cliqueiAcheiData || !timeManager.isReady || testEnvPreventOpenAndCloseAlertsWasSetted())
      return;

    const alerts = buildAlerts();

    if (timeManager.notOpenToday()) {
      alerts.isClosed();
      return;
    }

    if (!timeManager.openAt || !timeManager.closeAt) return;

    if (timeManager.hasPassedBusinessHours()) {
      if (!alertAboutClosingJustAppeared.current) alerts.isClosed();
      alertAboutClosingJustAppeared.current = false;
      return;
    }

    if (timeManager.isOpen()) setupRestaurantClosing();
    else setupRestaurantOpening();

    updateDataTimeout.current = setupUpdateDataTimer(
      timeManager.isOpen() ? timeManager.milliUntilClose() : timeManager.milliUntilOpen()
    );

    function setupRestaurantClosing() {
      console.log(`Its open. It will close at ${timeManager.closeAt?.toLocaleString()}`);
      console.log(`Time until close: ${timeLabel(timeManager.milliUntilClose())}`);

      if (timeManager.milliUntilClose() <= MIN_INTERVAL_TO_ALERT) {
        alerts.closingIn(timeManager.milliUntilClose());
      } else {
        const milliUntilAlert = timeManager.milliUntilClose() - MIN_INTERVAL_TO_ALERT;
        alertTimeout.current = setupAlertTimer(milliUntilAlert, () => alerts.closingIn(MIN_INTERVAL_TO_ALERT));
      }
    }

    function setupRestaurantOpening() {
      console.log(`Its closed. It will open at ${timeManager.openAt?.toLocaleString()}`);
      console.log(`Time until open: ${timeLabel(timeManager.milliUntilOpen())}`);

      if (timeManager.milliUntilOpen() <= MIN_INTERVAL_TO_ALERT) {
        alerts.openingIn(timeManager.milliUntilOpen());
      } else {
        const milliUntilAlert = timeManager.milliUntilOpen() - MIN_INTERVAL_TO_ALERT;
        alertTimeout.current = setupAlertTimer(milliUntilAlert, () => alerts.openingIn(MIN_INTERVAL_TO_ALERT));
        alerts.isClosed();
      }
    }

    function setupAlertTimer(when: number, alertFn: () => void) {
      console.log(`Scheduling timer to alert ${timeLabel(when)} from now`);
      return window.setTimeout(() => alertFn(), when);
    }

    function setupUpdateDataTimer(when: number) {
      console.log(`Will update ${timeLabel(when)} from now`);
      const willOpen = !timeManager.isOpen();

      return window.setTimeout(async () => {
        if (!cliqueiAcheiData) return;

        console.log(`Restaurant has ${willOpen ? 'opened' : 'closed'}`);

        willOpen ? alerts.justOpened() : alerts.justClosed();
        alertAboutClosingJustAppeared.current = willOpen === false;

        timeManager.revalidate();
      }, when);
    }

    function buildAlerts() {
      const restaurantName = cliqueiAcheiData?.NomePuro ?? '';
      const delay = (fn: () => void) => setTimeout(fn, 600);
      return {
        justOpened() {
          delay(() => {
            dialogRef.current?.alert({ message: AlertsMessagesBuilder.createJustOpenedMessage(restaurantName) });
          });
        },
        justClosed() {
          delay(() => {
            dialogRef.current?.alert({ message: AlertsMessagesBuilder.createJustClosedMessage(restaurantName) });
          });
        },
        openingIn(timeInMilli: number) {
          delay(() => {
            dialogRef.current?.alert({
              message: AlertsMessagesBuilder.createOpeningMessage(timeInMilli, restaurantName),
            });
          });
        },
        closingIn(timeInMilli: number) {
          delay(() => {
            dialogRef.current?.alert({
              message: AlertsMessagesBuilder.createClosingMessage(timeInMilli, restaurantName),
            });
          });
        },
        isClosed() {
          delay(() => {
            dialogRef.current?.alert({
              title: 'Estamos fechados',
              message: 'No momento estamos fora do nosso horário de atendimento.',
            });
          });
        },
      };
    }

    return () => {
      window.clearTimeout(alertTimeout.current);
      window.clearTimeout(updateDataTimeout.current);
    };
  }, [cliqueiAcheiData, isOpen, shouldIgnoreSetup]);
}

function testEnvPreventOpenAndCloseAlertsWasSetted() {
  return window?.Cypress?.env('prevent-close-and-open-alerts') === 'true';
}

function minutes(milliseconds: number) {
  return Math.ceil(milliseconds / (1000 * 60));
}

export const AlertsMessagesBuilder = {
  createClosingMessage(timeUntilCloseInMilli: number, restaurantName: string) {
    const minutesTime = minutes(timeUntilCloseInMilli);
    const minutesLabel = minutesTime === 1 ? 'minuto' : 'minutos';
    return `Atenção! ${restaurantName} fechará daqui ${minutesTime} ${minutesLabel}. Faça seu pedido antes que feche`;
  },
  createOpeningMessage(timeUntilOpenInMilli: number, restaurantName: string) {
    const minutesTime = minutes(timeUntilOpenInMilli);
    const minutesLabel = minutesTime === 1 ? 'minuto' : 'minutos';
    return `${restaurantName} abrirá daqui ${minutesTime} ${minutesLabel}. Assim que abrir você poderá enviar seu pedido.`;
  },
  createJustOpenedMessage(restaurantName: string) {
    return `${restaurantName} acabou de abrir. Você já pode fazer seu pedido!`;
  },
  createJustClosedMessage(restaurantName: string) {
    return `${restaurantName} acabou de fechar. Os pedidos estão encerrados!`;
  },
};

const SECOND = 1000;
const MINUTE = SECOND * 60;
const HOUR = MINUTE * 60;
/**
 * Just for debug purposes
 * @param milliseconds
 * @returns Time label like 08h:30m:20s
 */
export function timeLabel(milliseconds: number) {
  const isNegative = milliseconds < 0;
  milliseconds = Math.abs(milliseconds);

  const hours = Math.floor(milliseconds / HOUR);
  milliseconds -= hours * HOUR;
  const minutes = Math.floor(milliseconds / MINUTE);
  milliseconds -= minutes * MINUTE;
  const seconds = Math.floor(milliseconds / SECOND);

  const signal = isNegative ? '-' : '';
  return `${signal}${leftZeros(hours)}h:${leftZeros(minutes)}m:${leftZeros(seconds)}s`;

  function leftZeros(value: number) {
    return value.toFixed(0).padStart(2, '0');
  }
}
